// /////////////////////////////////////////////////////////////
// [ファイル] CodeViewer.tsx
// [概要] ソース品質管理プラットフォームの中ペイン。
//        選択シンボルのパンくず・シグネチャ・コード断片・AI 要約を表示する。
//        フェーズ1 (MVP) はコードを <pre> で表示する。シンタックス
//        ハイライト (react-syntax-highlighter) は依存追加後に差し替える。
// /////////////////////////////////////////////////////////////
import React from 'react';
import { Box, Typography, Paper, Breadcrumbs, Chip } from '@mui/material';
import Markdown from 'markdown-to-jsx';
import * as Icons from '../../constants/icons';
import { SourceSymbol, SYMBOL_KIND_LABEL } from '../../types/sourceQuality';

interface CodeViewerProps {
  symbol: SourceSymbol | null;
}

export default function CodeViewer({ symbol }: CodeViewerProps) {
  if (!symbol) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.disabled',
          gap: 1,
        }}
      >
        <Icons.Code sx={{ fontSize: 40 }} />
        <Typography variant="body2">左のツリーからシンボルを選択してください</Typography>
      </Box>
    );
  }

  const segments = symbol.file_path.split('/');

  return (
    <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
      {/* パンくず */}
      <Breadcrumbs separator="/" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
        {segments.map((seg, i) => (
          <Typography
            key={i}
            variant="caption"
            sx={{
              color: i === segments.length - 1 ? 'text.primary' : 'text.disabled',
              fontFamily: 'monospace',
              fontSize: '0.7rem',
            }}
          >
            {seg}
          </Typography>
        ))}
      </Breadcrumbs>

      {/* シンボル名 + 種別 + 複雑度 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
          {symbol.name}
        </Typography>
        <Chip label={SYMBOL_KIND_LABEL[symbol.kind]} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
        {symbol.complexity != null && (
          <Chip
            label={`複雑度 ${symbol.complexity}`}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
        )}
      </Box>

      {symbol.signature && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontFamily: 'monospace',
            color: 'text.secondary',
            mb: 1,
            wordBreak: 'break-all',
          }}
        >
          {symbol.signature}
        </Typography>
      )}

      {/* コード断片 */}
      <Paper
        variant="outlined"
        sx={{
          bgcolor: '#f8f9fa',
          borderRadius: 1.5,
          overflow: 'auto',
          mb: 2,
        }}
      >
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            fontFamily: 'monospace',
            fontSize: '0.78rem',
            lineHeight: 1.55,
            whiteSpace: 'pre',
          }}
        >
          {symbol.code_snapshot}
        </Box>
      </Paper>

      {/* AI 要約 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          borderRadius: 1.5,
          bgcolor: '#f3f6fb',
          border: '1px solid',
          borderColor: '#e3eaf3',
        }}
      >
        <Icons.AutoAwesome sx={{ fontSize: 18, color: '#5c7c9e', mt: 0.25 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#5c7c9e', display: 'block', mb: 0.25 }}>
            AI 要約
          </Typography>
          {symbol.ai_summary ? (
            <Box sx={{ fontSize: '0.8rem', lineHeight: 1.6, '& p': { m: 0 } }}>
              <Markdown>{symbol.ai_summary}</Markdown>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>
              未分析です。右パネルの「AI 再分析」で生成できます（フェーズ2）。
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
